import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Calendar } from "react-date-range";
import { VARIANTS_AGE_FILTER } from "../../../helpers/constants";
import {
  IGProduct,
  IGVariant,
  TAgeFilter,
  TRaceTypeFilter,
  TTimeFilter,
  TTrackTypes,
  IGCollectionsProduct,
  TPriceFilterOptions,
  TActiveSortOption,
  TSelectedVariants,
} from "../../../types";
import { DateTime } from "luxon";
import { ChevronLeftIcon, ChevronRightIcon, ClockIcon, CurrencyDollarIcon, XIcon, CalendarIcon } from "@heroicons/react/outline";
import { VariantsBlock } from "./VariantsBlocks";
import { useTrackContext } from "../../../hooks/useTrackContext";
import GoogleAnalytics from "../../GoogleAnalytics";
import { logGAEvent } from "../../GoogleAnalytics";
import { convertTo12HourFormat, getRaceHourFromSku, getStartTimeAndEndTime, hasDifferentVariantSelectedTime, sortBySku } from "../../../helpers/utils";
import { LoadingAnimationURL, BattleKartLogoURL } from "../../../consts/assetUrls";
import NoRacesPopup from "./NoRacesPopup";
import { FilterBoxHeader } from "./FilterBoxHeader";
import getCartData from "../../../utils/getCartData";
import { fetchProductByHandle, fetchCollectionsByHandle } from "../../../utils/productOperations";
import { getAdminIdFromStoreFrontId } from "../../../helpers/shopify";
// Import Swiper components
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";

// Import Swiper styles
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

function getOrdinal(number: number) {
  const lastDigitOfNumber = ![11, 12, 13].includes(number) ? number % 10 : number;

  switch (lastDigitOfNumber) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
}

// Helper function to parse date from product title
function parseDateFromProductTitle(title: string): Date {
  // Parse date from various product title formats:
  // "Wednesday, 2nd July 2025 | 10 Minute Race"
  // "Wednesday, 2nd July 2025 | 7 Minute Race | Junior Track"
  // "Wednesday, 2nd July 2025 | 10 Minute Mini Track"
  // "Wednesday, 2nd July 2025 | BattleKart | 10 Minute Session"
  const titleParts = title.split("|")[0].trim();
  const dateMatch = titleParts.match(/(\w+),\s*(\d+)(?:st|nd|rd|th)\s+(\w+)\s+(\d{4})/);

  if (dateMatch) {
    const [, , day, month, year] = dateMatch;
    // Use Luxon to parse the date more robustly
    const dateString = `${day} ${month} ${year}`;
    const parsedDateTime = DateTime.fromFormat(dateString, "d MMMM yyyy");

    if (parsedDateTime.isValid) {
      return parsedDateTime.toJSDate();
    }
  }

  // Fallback to current date if parsing fails
  return DateTime.local().toJSDate();
}

const TIME_RANGE: [number, number][] = [
  [7, 11], // MORNING: 7:00am to 11:00am
  [11, 15], // LUNCH: 11:00am to 3:00pm
  [15, 19], // ARVO: 3:00pm to 7:00pm
  [19, 23], // EVENING: 7:00pm to 11:00pm
  [23, 25], // LATE: 11:00pm to 1:00am (next day)
];

const TIME_RANGE_VIRTUAL_REALITY: [number, number][] = [
  [0, 12.39],
  [13, 18.39],
  [18, 23.59],
];
const TIME_RANGE_MINI_TRACK: [number, number][] = [
  [7, 11],
  [11, 15],
  [15, 19],
  [19, 23],
  [23, 25],
];

const TIME_RANGE_BATTLE_KART_TRACK: [number, number][] = [
  [7, 11],
  [11, 15],
  [15, 19],
  [19, 23],
  [23, 25],
];

const TIME_RANGE_JUNIOR_TRACK: [number, number][] = [
  [7, 11],
  [11, 15],
  [15, 19],
  [19, 23],
  [23, 25],
];

export function ProductSelection({
  selectProductOnly,
  onVariantSelection,
}: {
  selectProductOnly?: boolean;
  onVariantSelection?: (isAdded: boolean, variant: IGVariant, selectedVariants: IGVariant[]) => void;
}) {
  const {
    mainTrackSelection,
    miniTrackSection,
    vrSelection,
    juniorTrackSelection,
    battleKartSelection,
    productRenderedLocation,
    appHostedAt,
    trackSelectionOption,
    dispatch,
    headlessCartInfo,
    isShopifyCartVariantsLoaded,
  } = useTrackContext();

  const track: TTrackTypes = (() => {
    switch (trackSelectionOption.title) {
      case "MAIN_TRACK":
        return "MAIN_TRACK";
      case "MINI_TRACK":
        return "MINI_TRACK";
      case "VIRTUAL_REALITY":
        return "VIRTUAL_REALITY";
      case "JUNIOR_TRACK":
        return "JUNIOR_TRACK";
      case "BATTLE_KART_TRACK":
        return "BATTLE_KART_TRACK";
      default: {
        const pathSuffix = window?.location?.pathname?.split?.("/")?.slice?.(-1)?.[0]?.slice?.(-2);
        switch (pathSuffix) {
          case "mi":
            return "MINI_TRACK";
          case "vr":
            return "VIRTUAL_REALITY";
          case "jt":
            return "JUNIOR_TRACK";
          case "bk":
            return "BATTLE_KART_TRACK";
          default:
            return "MAIN_TRACK";
        }
      }
    }
  })();

  const tracksProductHandle = useCallback(
    (dateString: string) => {
      if (track === "MAIN_TRACK") {
        return `${dateString}mt`;
      } else if (track === "MINI_TRACK") {
        return `0${dateString}mi`;
      } else if (track === "VIRTUAL_REALITY") {
        return `${dateString}vr`;
      } else if (track === "JUNIOR_TRACK") {
        return `${dateString}jt`;
      } else if (track === "BATTLE_KART_TRACK") {
        return `${dateString}bk`;
      }
    },
    [track]
  );

  const [stayOnSelectedDate, setStayOnSelectedDate] = useState<string>("");
  const mainTrackSelectedDate = mainTrackSelection.variantsAndDate.selectedDate;
  const miniTrackSelectedDate = miniTrackSection.variantsAndDate.selectedDate;
  const vrSelectedDate = vrSelection.variantsAndDate.selectedDate;
  const juniorTrackSelectedDate = juniorTrackSelection.variantsAndDate.selectedDate;
  const battleKartSelectedDate = battleKartSelection.variantsAndDate.selectedDate;

  useEffect(() => {
    if (track === "MAIN_TRACK" && mainTrackSelectedDate) {
      setStayOnSelectedDate(mainTrackSelectedDate.toDateString());
    }
    if (track === "MINI_TRACK" && miniTrackSelectedDate) {
      setStayOnSelectedDate(miniTrackSelectedDate.toDateString());
    }
    if (track === "VIRTUAL_REALITY" && vrSelectedDate) {
      setStayOnSelectedDate(vrSelectedDate.toDateString());
    }
    if (track === "JUNIOR_TRACK" && juniorTrackSelectedDate) {
      setStayOnSelectedDate(juniorTrackSelectedDate.toDateString());
    }
    if (track === "BATTLE_KART_TRACK" && battleKartSelectedDate) {
      setStayOnSelectedDate(battleKartSelectedDate.toDateString());
    }
  }, [mainTrackSelectedDate, miniTrackSelectedDate, track, vrSelectedDate, juniorTrackSelectedDate, battleKartSelectedDate]);

  const dateFromUrl = DateTime.fromFormat(window?.location?.pathname?.split?.("/")?.slice?.(-1)?.[0]?.slice(0, -2), "yyLLdd");

  const initialProductHandle = useMemo(() => {
    if (stayOnSelectedDate && stayOnSelectedDate !== "") {
      return tracksProductHandle(DateTime.fromJSDate(new Date(stayOnSelectedDate)).toFormat("yyLLdd"));
    } else if (dateFromUrl && dateFromUrl.isValid) {
      return tracksProductHandle(dateFromUrl.toFormat("yyLLdd"));
    } else {
      return tracksProductHandle(DateTime.now().toFormat("yyLLdd"));
    }
  }, [stayOnSelectedDate, dateFromUrl, tracksProductHandle]);

  const [selectedDate, setSelectedDate] = useState<Date | null>(() => {
    if (dateFromUrl && dateFromUrl.isValid) {
      return dateFromUrl.toJSDate();
    } else {
      return DateTime.local().startOf("day").toJSDate();
    }
  });

  const [tempSelectedMonth, setTempSelectedMonth] = useState<Date>(selectedDate || new Date());

  const [productHandle, setProductHandle] = useState(initialProductHandle ? initialProductHandle : tracksProductHandle(DateTime.now().toFormat("yyLLdd")));
  const [showDateRange, setShowDateRange] = useState<boolean>(false);
  const [showDesktopCalendar, setShowDesktopCalendar] = useState<boolean>(false);
  const [product, setProduct] = useState<IGProduct | null>(null);
  const [collectionProducts, setCollectionProducts] = useState<IGCollectionsProduct[] | null>(null);
  const [selectedVariants, setSelectedVariants] = useState<IGVariant[]>([]);
  const [numSeats] = useState(1 as number);
  const [timeFilter, setTimeFilter] = useState<TTimeFilter>("MORNING");
  const [raceTypeFilter] = useState<TRaceTypeFilter>(mainTrackSelection.raceType || "STANDARD");
  const [priceFilter, setPriceFilter] = useState<TPriceFilterOptions>("19-24");
  const [activeSortType, setActiveSortType] = useState<TActiveSortOption>("time");
  const ageFilter: TAgeFilter = useMemo(() => mainTrackSelection.ageGroup, [mainTrackSelection.ageGroup]);
  const calenderRef = useRef<HTMLDivElement | null>(null);
  const mainTrackSelectedSessions = mainTrackSelection.variantsAndDate.selectedSessionTime;
  const miniTrackSelectedSessions = miniTrackSection.variantsAndDate.selectedSessionTime;
  const virtualRealitySelectedSessions = vrSelection.variantsAndDate.selectedSessionTime;
  const juniorTrackSelectedSessions = juniorTrackSelection.variantsAndDate.selectedSessionTime;
  const battleKartSelectedSessions = battleKartSelection.variantsAndDate.selectedSessionTime;
  const [loading, setLoading] = useState<boolean>(true);
  const [isNoRacesPopupShowing, setIsNoRacesPopupShowing] = useState<boolean>(false);
  const [isTouching, setIsTouching] = useState<boolean>(false);
  const touchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const touchStartXRef = useRef<number | null>(null);
  const touchCurrentXRef = useRef<number | null>(null);
  const swiperRef = useRef<SwiperType | null>(null);

  const filterOptions: { title: string; subTitle?: string; active: boolean; filter: TPriceFilterOptions | TTimeFilter }[] = useMemo(() => {
    if (activeSortType === "price") {
      if (track === "JUNIOR_TRACK" || track === "MINI_TRACK") {
        return [
          { title: "$9-$14", active: priceFilter === "9-14", filter: "9-14" as TPriceFilterOptions },
          { title: "$19-$24", active: priceFilter === "19-24", filter: "19-24" as TPriceFilterOptions },
          { title: "$29-$34", active: priceFilter === "29-34", filter: "29-34" as TPriceFilterOptions },
          { title: "$39+", active: priceFilter === "39-", filter: "39-" as TPriceFilterOptions },
        ];
      }
      return [
        { title: "$19-$24", active: priceFilter === "19-24", filter: "19-24" as TPriceFilterOptions },
        { title: "$29-$39", active: priceFilter === "29-39", filter: "29-39" as TPriceFilterOptions },
        { title: "$44-$49", active: priceFilter === "44-49", filter: "44-49" as TPriceFilterOptions },
        { title: "$54+", active: priceFilter === "54-", filter: "54-" as TPriceFilterOptions },
      ];
    }

    const getTimeFilterLabel = (timeFilter: TTimeFilter, track: TTrackTypes) => {
      const { startHour, endHour } = getStartTimeAndEndTime(timeFilter, track);

      return `${convertTo12HourFormat(startHour)} - ${convertTo12HourFormat(endHour)}`;
    };

    switch (track) {
      case "MAIN_TRACK":
        return [
          { title: "Morning", subTitle: "7:00am - 11:00am", active: timeFilter === "MORNING", filter: "MORNING" as TTimeFilter },
          { title: "Lunch", subTitle: "11:00am - 3:00pm", active: timeFilter === "LUNCH", filter: "LUNCH" as TTimeFilter },
          { title: "Arvo", subTitle: "3:00pm - 7:00pm", active: timeFilter === "ARVO", filter: "ARVO" as TTimeFilter },
          { title: "Evening", subTitle: "7:00pm - 11:00pm", active: timeFilter === "EVENING", filter: "EVENING" as TTimeFilter },
          { title: "Late", subTitle: "11:00pm - 1:00am", active: timeFilter === "LATE", filter: "LATE" as TTimeFilter },
        ];
      case "JUNIOR_TRACK":
        return [
          { title: "Morning", subTitle: "7:00am - 11:00am", active: timeFilter === "MORNING", filter: "MORNING" as TTimeFilter },
          { title: "Lunch", subTitle: "11:00am - 3:00pm", active: timeFilter === "LUNCH", filter: "LUNCH" as TTimeFilter },
          { title: "Arvo", subTitle: "3:00pm - 7:00pm", active: timeFilter === "ARVO", filter: "ARVO" as TTimeFilter },
          { title: "Evening", subTitle: "7:00pm - 11:00pm", active: timeFilter === "EVENING", filter: "EVENING" as TTimeFilter },
          { title: "Late", subTitle: "11:00pm - 1:00am", active: timeFilter === "LATE", filter: "LATE" as TTimeFilter },
        ];
      case "VIRTUAL_REALITY":
        return [
          { title: "Morning", subTitle: getTimeFilterLabel("MORNING", track), active: timeFilter === "MORNING", filter: "MORNING" as TTimeFilter },
          { title: "Afternoon", subTitle: getTimeFilterLabel("AFTERNOON", track), active: timeFilter === "AFTERNOON", filter: "AFTERNOON" as TTimeFilter },
          { title: "Evening", subTitle: getTimeFilterLabel("EVENING", track), active: timeFilter === "EVENING", filter: "EVENING" as TTimeFilter },
        ];
      case "BATTLE_KART_TRACK":
        return [
          { title: "Morning", subTitle: "7:00am - 11:00am", active: timeFilter === "MORNING", filter: "MORNING" as TTimeFilter },
          { title: "Lunch", subTitle: "11:00am - 3:00pm", active: timeFilter === "LUNCH", filter: "LUNCH" as TTimeFilter },
          { title: "Arvo", subTitle: "3:00pm - 7:00pm", active: timeFilter === "ARVO", filter: "ARVO" as TTimeFilter },
          { title: "Evening", subTitle: "7:00pm - 11:00pm", active: timeFilter === "EVENING", filter: "EVENING" as TTimeFilter },
          { title: "Late", subTitle: "11:00pm - 1:00am", active: timeFilter === "LATE", filter: "LATE" as TTimeFilter },
        ];
      default:
        return [
          { title: "Morning", subTitle: "7:00am - 11:00am", active: timeFilter === "MORNING", filter: "MORNING" as TTimeFilter },
          { title: "Lunch", subTitle: "11:00am - 3:00pm", active: timeFilter === "LUNCH", filter: "LUNCH" as TTimeFilter },
          { title: "Arvo", subTitle: "3:00pm - 7:00pm", active: timeFilter === "ARVO", filter: "ARVO" as TTimeFilter },
          { title: "Evening", subTitle: "7:00pm - 11:00pm", active: timeFilter === "EVENING", filter: "EVENING" as TTimeFilter },
          { title: "Late", subTitle: "11:00pm - 1:00am", active: timeFilter === "LATE", filter: "LATE" as TTimeFilter },
        ];
    }
  }, [track, timeFilter, activeSortType, priceFilter]);

  const handleFilterOptionClick = (option: (typeof filterOptions)[number]) => () => {
    if (activeSortType === "price") {
      setPriceFilter(option.filter as TPriceFilterOptions);
    } else {
      setTimeFilter(option.filter as TTimeFilter);
    }

    // Find the index of the clicked filter and update the active slide
    const availableSlides = filterOptions
      .map((filterOption) => {
        const variantsForThisFilter = filteredVariantsMap[filterOption.filter] || [];
        return {
          filter: filterOption.filter,
          hasContent: variantsForThisFilter.length > 0 && variantsForThisFilter.some((variant) => variant.availableForSale),
        };
      })
      .filter((slide) => slide.hasContent);

    const slideIndex = availableSlides.findIndex((slide) => slide.filter === option.filter);
    if (slideIndex !== -1 && swiperRef.current) {
      swiperRef.current.slideTo(slideIndex, 300);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      dispatch({
        type: "SET_PRODUCT_LOADING",
        payload: {
          isProductLoading: true,
        },
      });

      try {
        // Fetch both product and collections data in parallel
        const [product, collectionProducts] = await Promise.all([
          productHandle ? fetchProductByHandle(productHandle) : Promise.resolve(null),
          fetchCollectionsByHandle("handle:main-track"),
        ]);

        if (product) {
          setProduct(product);
        }

        if (collectionProducts) {
          setCollectionProducts(collectionProducts);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
        dispatch({
          type: "SET_PRODUCT_LOADING",
          payload: {
            isProductLoading: false,
          },
        });
      }
    };

    fetchData();
  }, [ageFilter, productHandle, dispatch]);

  const disableVariantsDate = useMemo(() => {
    if (track === "MAIN_TRACK" && ageFilter === "16+") {
      const disabledDates = collectionProducts?.map((cProducts: IGCollectionsProduct) => {
        const hasAgeRequirementVariant = cProducts.variants.nodes.some((variant) => {
          return (
            variant.availableForSale &&
            variant.quantityAvailable !== 0 &&
            variant.selectedOptions.find((option) => option.name.startsWith("Age Requirement") && option.value === "Ages 16+")
          );
        });
        // Extract date from the title
        const varianttitleToDate = cProducts.title
          .split("|")[0]
          .replace(/(\d+)(st|nd|rd|th)/, "$1")
          .split(",")[1]
          .trim();

        return hasAgeRequirementVariant ? null : DateTime.fromFormat(varianttitleToDate, "d MMMM yyyy").toJSDate();
      });

      return disabledDates ? disabledDates.filter((date) => date !== null) : [];
    }
    if (track === "MAIN_TRACK" && ageFilter === "12-15") {
      const disabledDates = collectionProducts?.map((cProducts: IGCollectionsProduct) => {
        const hasAgeRequirementVariant = cProducts.variants.nodes.some((variant) => {
          return (
            variant.availableForSale &&
            variant.quantityAvailable !== 0 &&
            variant.selectedOptions.find((option) => option.name.startsWith("Age Requirement") && option.value === "Ages 12-15")
          );
        });
        // Extract date from the title
        const varianttitleToDate = cProducts.title
          .split("|")[0]
          .replace(/(\d+)(st|nd|rd|th)/, "$1")
          .split(",")[1]
          .trim();

        return hasAgeRequirementVariant ? null : DateTime.fromFormat(varianttitleToDate, "d MMMM yyyy").toJSDate();
      });

      return disabledDates ? disabledDates.filter((date) => date !== null) : [];
    }
    if (track === "MAIN_TRACK" && ageFilter === "Under 12") {
      const disabledDates = collectionProducts?.map((cProducts: IGCollectionsProduct) => {
        const hasAgeRequirementVariant = cProducts.variants.nodes.some((variant) => {
          return (
            variant.availableForSale &&
            variant.quantityAvailable !== 0 &&
            variant.selectedOptions.find((option) => option.name.startsWith("Age Requirement") && option.value === "140cm+ | Ages under 12")
          );
        });
        // Extract date from the title
        const varianttitleToDate = cProducts.title
          .split("|")[0]
          .replace(/(\d+)(st|nd|rd|th)/, "$1")
          .split(",")[1]
          .trim();

        return hasAgeRequirementVariant ? null : DateTime.fromFormat(varianttitleToDate, "d MMMM yyyy").toJSDate();
      });

      return disabledDates ? disabledDates.filter((date) => date !== null) : [];
    }
  }, [ageFilter, collectionProducts, track]) as Date[];

  useEffect(() => {
    setProductHandle(tracksProductHandle(DateTime.fromJSDate(selectedDate !== null ? selectedDate : new Date()).toFormat("yyLLdd")));
  }, [disableVariantsDate, selectedDate, setProductHandle, tracksProductHandle]);

  const availableVariantsDate = useMemo(() => {
    if (track === "MAIN_TRACK" && ageFilter === "16+") {
      const dateMap: Record<string, Date[]> = (collectionProducts ?? []).reduce((accumulator, cProducts) => {
        const hasAgeRequirementVariant = cProducts.variants.nodes.some((variant) => {
          return (
            variant.availableForSale &&
            variant.quantityAvailable !== 0 &&
            variant.selectedOptions.find((option) => option.name.startsWith("Age Requirement") && option.value === "Ages 16+")
          );
        });

        if (hasAgeRequirementVariant) {
          const varianttitleToDate = cProducts.title
            .split("|")[0]
            .replace(/(\d+)(st|nd|rd|th)/, "$1")
            .split(",")[1]
            .trim();

          const date = DateTime.fromFormat(varianttitleToDate, "d MMMM yyyy").toJSDate();

          if (DateTime.fromJSDate(date).isValid) {
            // Check if date is valid before adding
            const monthKey = `${date.getMonth() + 1}-${date.getFullYear()}`;
            accumulator[monthKey] = accumulator[monthKey] || [];
            accumulator[monthKey].push(date);
          }
        }

        return accumulator;
      }, {} as Record<string, Date[]>);

      // Convert the object into an array
      const result = Object.entries(dateMap).map(([monthKey, dates]) => ({ monthKey, dates }));

      return result;
    }
    if (track === "MAIN_TRACK" && ageFilter === "12-15") {
      const dateMap: Record<string, Date[]> = (collectionProducts ?? []).reduce((accumulator, cProducts) => {
        const hasAgeRequirementVariant = cProducts.variants.nodes.some((variant) => {
          return (
            variant.availableForSale &&
            variant.quantityAvailable !== 0 &&
            variant.selectedOptions.find((option) => option.name.startsWith("Age Requirement") && option.value === "Ages 12-15")
          );
        });

        if (hasAgeRequirementVariant) {
          const varianttitleToDate = cProducts.title
            .split("|")[0]
            .replace(/(\d+)(st|nd|rd|th)/, "$1")
            .split(",")[1]
            .trim();

          const date = DateTime.fromFormat(varianttitleToDate, "d MMMM yyyy").toJSDate();

          if (DateTime.fromJSDate(date).isValid) {
            // Check if date is valid before adding
            const monthKey = `${date.getMonth() + 1}-${date.getFullYear()}`;
            accumulator[monthKey] = accumulator[monthKey] || [];
            accumulator[monthKey].push(date);
          }
        }

        return accumulator;
      }, {} as Record<string, Date[]>);

      // Convert the object into an array
      const result = Object.entries(dateMap).map(([monthKey, dates]) => ({ monthKey, dates }));

      return result;
    }
    if (track === "MAIN_TRACK" && ageFilter === "Under 12") {
      const dateMap: Record<string, Date[]> = (collectionProducts ?? []).reduce((accumulator, cProducts) => {
        const hasAgeRequirementVariant = cProducts.variants.nodes.some((variant) => {
          return (
            variant.availableForSale &&
            variant.quantityAvailable !== 0 &&
            variant.selectedOptions.find((option) => option.name.startsWith("Age Requirement") && option.value.toLowerCase().includes("ages under 12"))
          );
        });

        if (hasAgeRequirementVariant) {
          const varianttitleToDate = cProducts.title
            .split("|")[0]
            .replace(/(\d+)(st|nd|rd|th)/, "$1")
            .split(",")[1]
            .trim();

          const date = DateTime.fromFormat(varianttitleToDate, "d MMMM yyyy").toJSDate();

          if (DateTime.fromJSDate(date).isValid) {
            // Check if date is valid before adding
            const monthKey = `${date.getMonth() + 1}-${date.getFullYear()}`;
            accumulator[monthKey] = accumulator[monthKey] || [];
            accumulator[monthKey].push(date);
          }
        }

        return accumulator;
      }, {} as Record<string, Date[]>);

      // Convert the object into an array
      const result = Object.entries(dateMap).map(([monthKey, dates]) => ({ monthKey, dates }));

      return result;
    }
    return [];
  }, [ageFilter, collectionProducts, track]);

  const handleSelect = (date: Date) => {
    if (selectedDate === null || !DateTime.fromJSDate(selectedDate).hasSame(DateTime.fromJSDate(date), "day")) {
      setSelectedDate(date);
      setTempSelectedMonth(date); // Update temp month to match selected date
      setShowDateRange(false); // Hide calendar when date is selected
    }
  };

  const handlePrevDayClick = () => {
    if (selectedDate) {
      const prevDay = DateTime.fromJSDate(selectedDate).minus({ days: 1 });
      if (prevDay >= DateTime.local().startOf("day")) {
        setSelectedDate(prevDay.toJSDate());
      }
    }
  };

  const handleNextDayClick = () => {
    if (selectedDate) {
      const nextDay = DateTime.fromJSDate(selectedDate).plus({ days: 1 });
      setSelectedDate(nextDay.toJSDate());
    }
  };

  const handlePrevMonthClick = () => {
    const currentDate = tempSelectedMonth ? DateTime.fromJSDate(tempSelectedMonth) : DateTime.local();
    const newMonth = currentDate.minus({ months: 1 });
    setTempSelectedMonth(newMonth.toJSDate());
  };

  const handleNextMonthClick = () => {
    const currentDate = tempSelectedMonth ? DateTime.fromJSDate(tempSelectedMonth) : DateTime.local();
    const newMonth = currentDate.plus({ months: 1 });
    setTempSelectedMonth(newMonth.toJSDate());
  };

  useEffect(() => {
    if (stayOnSelectedDate) {
      const parsedDate = DateTime.fromJSDate(new Date(stayOnSelectedDate));
      const currentDate = DateTime.local();
      if (parsedDate.isValid && parsedDate >= currentDate) {
        setSelectedDate(parsedDate.toJSDate());
      }
    }
  }, [stayOnSelectedDate]);

  const getRaceTimeForVariant = useCallback(
    (variant: IGVariant) =>
      variant.selectedOptions.find((e) =>
        e.name.includes(track === "MAIN_TRACK" || track === "JUNIOR_TRACK" || track === "BATTLE_KART_TRACK" ? "Race Time" : track === "VIRTUAL_REALITY" ? "Time" : "Drive Time")
      )?.value || "",
    [track]
  );

  useEffect(() => {
    if (track === "MAIN_TRACK" && (ageFilter === "12-15" || ageFilter === "Under 12")) {
      const selectedTimestamp = selectedDate !== null ? selectedDate.getTime() : null;
      const isDateDisabled = disableVariantsDate.some((date) => date.getTime() === selectedTimestamp);

      if (isDateDisabled && availableVariantsDate.length > 0) {
        let firstAvailableDate = availableVariantsDate[0].dates[0];

        // Check if the first available date is in the future
        const currentDate = new Date();
        const firstAvailableTimestamp = firstAvailableDate.getTime();

        if (firstAvailableTimestamp <= currentDate.getTime()) {
          // Find the next available date
          const nextAvailableDate = availableVariantsDate.flatMap((variant) => variant.dates).find((date) => date.getTime() > currentDate.getTime());

          if (nextAvailableDate) {
            setSelectedDate(nextAvailableDate);
          }
        } else {
          // Use the first available date
          setSelectedDate(firstAvailableDate);
        }
      }
    }
  }, [ageFilter, availableVariantsDate, disableVariantsDate, selectedDate, track]);

  const filteredVariantsExceptTime = useMemo(() => {
    if (!product || !product.variants) {
      return [];
    }

    let variants = [...product.variants];

    const isToday = DateTime.fromJSDate(selectedDate as Date).hasSame(DateTime.now(), "day");
    const currentDateTime = DateTime.now();
    const currentTimeInMinutes = currentDateTime.hour * 60 + currentDateTime.minute;

    if (isToday) {
      variants = variants.map((variant: IGVariant) => {
        const match = variant.title.match(/(\d{1,2}:\d{2})([apAP][mM])/);
        const variantTime = match ? match[1] : "";

        if (variantTime && match) {
          const timeParts = variantTime.split(":");
          const hours = parseInt(timeParts[0]);
          // add 5 to minutes to disable the variant 5 minutes later
          const minutes = parseInt(timeParts[1]) + 5;
          const isPM = match[2].toLowerCase() === "pm";

          // Calculate variant time in minutes
          const variantTimeInMinutes = isPM ? ((hours % 12) + 12) * 60 + minutes : hours * 60 + minutes;

          if (variantTimeInMinutes < currentTimeInMinutes) {
            variant.availableForSale = false;
          }
        }
        return variant;
      });
    }

    variants = variants.sort((a, b) => {
      if (!a?.sku) return 1;
      if (!b?.sku) return -1;
      return a?.sku.localeCompare(b?.sku);
    });

    // Filter Age for Main Track
    variants =
      track === "MINI_TRACK" || track === "VIRTUAL_REALITY" || track === "JUNIOR_TRACK" || track === "BATTLE_KART_TRACK"
        ? variants
        : variants.map((variant) => {
            variant = JSON.parse(JSON.stringify(variant));
            if (raceTypeFilter) {
              const variantType = variant.selectedOptions.find((e) => e.name.startsWith("Race Type"))?.value || "";
              if (raceTypeFilter !== variantType.toUpperCase()) {
                variant.availableForSale = false;
              }
            }
            if (ageFilter) {
              const ageReq = variant.selectedOptions.find((e) => e.name.startsWith("Age Requirement"))?.value || "";
              if (!ageReq.includes(VARIANTS_AGE_FILTER[ageFilter])) {
                variant.availableForSale = false;
              }
            }
            if (numSeats) {
              if (+variant.quantityAvailable < numSeats) {
                variant.availableForSale = false;
              }
            }
            return variant;
          });

    return variants;
  }, [product, selectedDate, track, raceTypeFilter, ageFilter, numSeats]);

  const filteredVariantsMap: Record<string, IGVariant[]> = useMemo(() => {
    const map: Record<string, IGVariant[]> = {};

    if (!filteredVariantsExceptTime.length) {
      return map;
    }

    // Group variants based on active sort type
    if (activeSortType === "time") {
      // Group by time slots
      filterOptions.forEach((option) => {
        const { startHour, endHour } = getStartTimeAndEndTime(option.filter as TTimeFilter, track);
        const timeSlotVariants = filteredVariantsExceptTime.filter((variant) => {
          const variantHour = getRaceHourFromSku(variant?.sku);
          return variantHour >= startHour && variantHour < endHour;
        });
        map[option.filter] = timeSlotVariants;
      });
    } else if (activeSortType === "price") {
      // Group by price brackets
      filterOptions.forEach((option) => {
        const [lowerPrice, higherPrice] = option.filter.split("-").map(Number);
        const priceSlotVariants = filteredVariantsExceptTime.filter((variant) => {
          const variantPrice = Number(variant.priceV2.amount);
          return variantPrice >= lowerPrice && variantPrice <= (higherPrice || Infinity) && variant.availableForSale && variant.quantityAvailable > 0;
        });
        map[option.filter] = priceSlotVariants;
      });
    }

    return map;
  }, [filteredVariantsExceptTime, activeSortType, track, filterOptions]);

  // Calculate the initial slide index based on the active filter
  const getInitialSlideIndex = useCallback(() => {
    const currentFilter = activeSortType === "price" ? priceFilter : timeFilter;
    const availableSlides = filterOptions
      .map((filterOption) => {
        const variantsForThisFilter = filteredVariantsMap[filterOption.filter] || [];
        return {
          filter: filterOption.filter,
          hasContent: variantsForThisFilter.length > 0 && variantsForThisFilter.some((variant) => variant.availableForSale),
        };
      })
      .filter((slide) => slide.hasContent);

    const activeSlideIndex = availableSlides.findIndex((slide) => slide.filter === currentFilter);
    return activeSlideIndex >= 0 ? activeSlideIndex : 0;
  }, [activeSortType, priceFilter, timeFilter, filterOptions, filteredVariantsMap]);

  const getTimeStampFromVariant = useCallback(
    (variant: IGVariant) => {
      let raceDate = variant?.sku?.split(".")?.[0];
      if (raceDate) {
        if (track === "MINI_TRACK") {
          raceDate = raceDate?.slice?.(1);
        }
        if (raceDate && !isNaN(raceDate as any)) {
          const timestamp = DateTime.fromFormat(raceDate, "yyMMddHHmm", {
            zone: "Australia/Sydney",
          }).toMillis();
          return isNaN(timestamp) ? null : timestamp;
        }
      }
      return null;
    },
    [track]
  );

  const activeRace = useMemo(() => {
    return (product?.variants || []).reduce<IGVariant | null>((prev, curr) => {
      const currTs = getTimeStampFromVariant(curr);
      if (!prev) {
        // If prev is null (first iteration), return the current variant
        return curr;
      } else {
        const prevTs = getTimeStampFromVariant(prev);
        // Check if either currTs or prevTs is null, and return the non-null value
        if (currTs === null && prevTs === null) {
          return curr;
        } else if (currTs === null) {
          return prev;
        } else if (prevTs === null) {
          return curr;
        } else {
          // Compare the timestamps and return the variant with the latest timestamp
          return currTs > prevTs ? curr : prev;
        }
      }
    }, null);
  }, [getTimeStampFromVariant, product?.variants]);

  const dateDiff = useMemo(() => (selectedDate !== null ? selectedDate.getTime() : new Date().getTime() - DateTime.local().startOf("day").toMillis()), [selectedDate]);

  const formattedDateString: string = useMemo(() => {
    const baseDate = selectedDate !== null ? DateTime.fromJSDate(selectedDate) : DateTime.now();
    return baseDate.toFormat("ccc, d") + getOrdinal(+baseDate.toFormat("d")) + baseDate.toFormat(" LLLL kkkk");
  }, [selectedDate]);

  const variantBlockClick = useCallback(
    (variant: IGVariant) => () => {
      const isVariantSelected = selectedVariants.some((v) => v.id === variant.id);

      let updatedVariants = [...selectedVariants];

      if (isVariantSelected) {
        updatedVariants = updatedVariants.filter((v) => v.id !== variant.id);
      } else {
        updatedVariants.push(variant);
      }
      setSelectedVariants(updatedVariants);

      if (onVariantSelection) {
        onVariantSelection(!isVariantSelected, variant, updatedVariants);
      }
    },
    [selectedVariants, onVariantSelection]
  );

  const handleOutsideClick = (event: MouseEvent) => {
    if (calenderRef.current && !calenderRef.current.contains(event.target as Node)) {
      setShowDateRange(false);
    }
  };

  const handleOutsideDesktopClick = (event: MouseEvent) => {
    if (calenderRef.current && !calenderRef.current.contains(event.target as Node)) {
      setShowDesktopCalendar(false);
    }
  };

  useEffect(() => {
    // Handle mobile date range outside click
    if (showDateRange) {
      document.addEventListener("mousedown", handleOutsideClick);
    } else {
      document.removeEventListener("mousedown", handleOutsideClick);
    }

    // Handle desktop calendar outside click
    if (showDesktopCalendar) {
      document.addEventListener("mousedown", handleOutsideDesktopClick);
    } else {
      document.removeEventListener("mousedown", handleOutsideDesktopClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("mousedown", handleOutsideDesktopClick);
    };
  }, [showDateRange, showDesktopCalendar]);

  useEffect(() => {
    // const currentDate = selectedDate && DateTime.fromJSDate(selectedDate).plus({ day: 1 }).toJSDate();
    const today = new Date();
    const currTime = today.getHours() + today.getMinutes() / 60;
    if (track === "MAIN_TRACK" && mainTrackSelectedSessions !== null) {
      setTimeFilter(mainTrackSelectedSessions as TTimeFilter);
    } else if (track === "MINI_TRACK" && miniTrackSelectedSessions !== null) {
      setTimeFilter(miniTrackSelectedSessions as TTimeFilter);
    } else if (track === "VIRTUAL_REALITY" && virtualRealitySelectedSessions !== null) {
      setTimeFilter(virtualRealitySelectedSessions as TTimeFilter);
    } else if (track === "JUNIOR_TRACK" && juniorTrackSelectedSessions !== null) {
      setTimeFilter(juniorTrackSelectedSessions as TTimeFilter);
    } else if (track === "BATTLE_KART_TRACK" && battleKartSelectedSessions !== null) {
      setTimeFilter(battleKartSelectedSessions as TTimeFilter);
    } else {
      if (track === "MAIN_TRACK") {
        TIME_RANGE.forEach((time, id) => {
          if (currTime > time[0] && currTime < time[1]) {
            switch (id) {
              case 0:
                setTimeFilter("MORNING");
                break;
              case 1:
                setTimeFilter("LUNCH");
                break;
              case 2:
                setTimeFilter("ARVO");
                break;
              case 3:
                setTimeFilter("EVENING");
                break;
              case 4:
                setTimeFilter("LATE");
                break;
            }
          }
        });
      } else if (track === "JUNIOR_TRACK") {
        TIME_RANGE_JUNIOR_TRACK.forEach((time, id) => {
          if (currTime > time[0] && currTime < time[1]) {
            switch (id) {
              case 0:
                setTimeFilter("MORNING");
                break;
              case 1:
                setTimeFilter("LUNCH");
                break;
              case 2:
                setTimeFilter("ARVO");
                break;
              case 3:
                setTimeFilter("EVENING");
                break;
              case 4:
                setTimeFilter("LATE");
                break;
            }
          }
        });
      } else if (track === "VIRTUAL_REALITY") {
        TIME_RANGE_VIRTUAL_REALITY.forEach((time, id) => {
          if (currTime > time[0] && currTime < time[1]) {
            switch (id) {
              case 0:
                setTimeFilter("MORNING");
                break;
              case 1:
                setTimeFilter("AFTERNOON");
                break;
              case 2:
                setTimeFilter("EVENING");
                break;
            }
          }
        });
      } else if (track === "BATTLE_KART_TRACK") {
        TIME_RANGE_BATTLE_KART_TRACK.forEach((time, id) => {
          if (currTime > time[0] && currTime < time[1]) {
            switch (id) {
              case 0:
                setTimeFilter("MORNING");
                break;
              case 1:
                setTimeFilter("LUNCH");
                break;
              case 2:
                setTimeFilter("ARVO");
                break;
              case 3:
                setTimeFilter("EVENING");
                break;
              case 4:
                setTimeFilter("LATE");
                break;
            }
          }
        });
      } else {
        TIME_RANGE_MINI_TRACK.forEach((time, id) => {
          if (currTime > time[0] && currTime < time[1]) {
            switch (id) {
              case 0:
                setTimeFilter("MORNING");
                break;
              case 1:
                setTimeFilter("LUNCH");
                break;
              case 2:
                setTimeFilter("ARVO");
                break;
              case 3:
                setTimeFilter("EVENING");
                break;
              case 4:
                setTimeFilter("LATE");
                break;
            }
          }
        });
      }
    }
  }, [battleKartSelectedSessions, juniorTrackSelectedSessions, mainTrackSelectedSessions, miniTrackSelectedSessions, track, virtualRealitySelectedSessions]);

  const getLatestSelectedVariant = useMemo(() => {
    const tracks = {
      MAIN_TRACK: mainTrackSelection,
      MINI_TRACK: miniTrackSection,
      VIRTUAL_REALITY: vrSelection,
      JUNIOR_TRACK: juniorTrackSelection,
      BATTLE_KART_TRACK: battleKartSelection,
    };

    const selectedTrack = tracks[track];
    const sortedVariants = selectedTrack?.variantsAndDate?.selectedVariants ? sortBySku(selectedTrack.variantsAndDate.selectedVariants, track) : [];

    if (sortedVariants.length > 0) {
      const latestSelectedVariant = sortedVariants[sortedVariants.length - 1];

      if (!hasDifferentVariantSelectedTime(sortedVariants, latestSelectedVariant)) {
        return latestSelectedVariant.variantsSelectedObject;
      }
    }

    return null;
  }, [mainTrackSelection, miniTrackSection, vrSelection, juniorTrackSelection, battleKartSelection, track]);

  const getNumberOfSelectedSeats = useMemo(() => {
    const seats = {
      MAIN_TRACK: mainTrackSelection.racesSeats.numberOfSeats,
      MINI_TRACK: miniTrackSection.racesSeats.numberOfSeats,
      VIRTUAL_REALITY: vrSelection.playersandsessions.numberOfPlayers,
      JUNIOR_TRACK: juniorTrackSelection.racesSeats.numberOfSeats,
      BATTLE_KART_TRACK: battleKartSelection.racesSeats.numberOfSeats,
    };
    return seats[track] || 0;
  }, [track, mainTrackSelection, miniTrackSection, vrSelection, juniorTrackSelection, battleKartSelection]);

  const getNumberOfSelectedRaces = useMemo(() => {
    const races = {
      MAIN_TRACK: mainTrackSelection.racesSeats.numberOfRaces,
      MINI_TRACK: miniTrackSection.racesSeats.numberOfRaces,
      VIRTUAL_REALITY: vrSelection.playersandsessions.numberOfSessions,
      JUNIOR_TRACK: juniorTrackSelection.racesSeats.numberOfRaces,
      BATTLE_KART_TRACK: battleKartSelection.racesSeats.numberOfRaces,
    };
    return races[track] || 0;
  }, [track, mainTrackSelection, miniTrackSection, vrSelection, juniorTrackSelection, battleKartSelection]);

  const upsellVariants = useMemo(() => {
    if (filteredVariantsExceptTime && getLatestSelectedVariant) {
      const productVariants = [...filteredVariantsExceptTime];
      const findVariantIndex = productVariants.findIndex((xVa: IGVariant) => xVa.id === getLatestSelectedVariant.id);

      if (findVariantIndex !== -1) {
        return productVariants
          .slice(findVariantIndex + 2, findVariantIndex + 7)
          .filter((xV: IGVariant) => xV.availableForSale && xV.quantityAvailable >= getNumberOfSelectedSeats)
          .map((item: IGVariant) => ({
            variantSelectedTime: new Date(),
            variantsSelectedObject: item,
          }));
      }
    }
    return [];
  }, [filteredVariantsExceptTime, getLatestSelectedVariant, getNumberOfSelectedSeats]);

  const noOfCells = useMemo(() => {
    if (track === "MAIN_TRACK") return 4;
    if (track === "JUNIOR_TRACK") return 4;
    if (track === "MINI_TRACK") return 4;
    if (track === "BATTLE_KART_TRACK") return 4;
    return 3; // Default for other tracks
  }, [track]);

  useEffect(() => {
    const tracks = {
      MAIN_TRACK: { selection: mainTrackSelection, actionType: "SET_DATE_AND_VARIANT" },
      MINI_TRACK: { selection: miniTrackSection, actionType: "SET_DATE_AND_VARIANT_MINI_TRACK" },
      VIRTUAL_REALITY: { selection: vrSelection, actionType: "SET_DATE_AND_VARIANT_VIRTUAL_REALITY" },
      JUNIOR_TRACK: { selection: juniorTrackSelection, actionType: "SET_DATE_AND_VARIANT_JUNIOR_TRACK" },
      BATTLE_KART_TRACK: { selection: battleKartSelection, actionType: "SET_DATE_AND_VARIANT_BATTLE_KART_TRACK" },
    };

    const currentTrack = tracks[track];

    if (currentTrack) {
      const sortedVariants = sortBySku(currentTrack.selection.variantsAndDate.selectedVariants, track);
      const latestSelectedVariant = sortedVariants[sortedVariants.length - 1];

      if (
        hasDifferentVariantSelectedTime(sortedVariants, latestSelectedVariant) &&
        JSON.stringify(currentTrack.selection.variantsAndDate.selectedVariantsForUpsell) !== JSON.stringify(upsellVariants)
      ) {
        dispatch({
          type: currentTrack.actionType as "SET_DATE_AND_VARIANT" | "SET_DATE_AND_VARIANT_MINI_TRACK" | "SET_DATE_AND_VARIANT_VIRTUAL_REALITY",
          payload: {
            variantsAndDate: {
              ...currentTrack.selection.variantsAndDate,
              selectedVariantsForUpsell: [],
            },
          },
        });
      } else if (JSON.stringify(currentTrack.selection.variantsAndDate.selectedVariantsForUpsell) !== JSON.stringify(upsellVariants)) {
        dispatch({
          type: currentTrack.actionType as "SET_DATE_AND_VARIANT" | "SET_DATE_AND_VARIANT_MINI_TRACK" | "SET_DATE_AND_VARIANT_VIRTUAL_REALITY",
          payload: {
            variantsAndDate: {
              ...currentTrack.selection.variantsAndDate,
              selectedVariantsForUpsell: upsellVariants,
            },
          },
        });
      }
    }
  }, [upsellVariants, dispatch, track, mainTrackSelection, miniTrackSection, vrSelection, juniorTrackSelection, battleKartSelection]);

  useEffect(() => {
    const hasAvailableVariant = filteredVariantsExceptTime.some((variant) => variant.availableForSale);

    // Not loading, product is not null, no available variants, and the date is not today
    if (!loading && product !== null && !hasAvailableVariant && !DateTime.fromJSDate(selectedDate as Date).hasSame(DateTime.now(), "day")) {
      setIsNoRacesPopupShowing(true);
    } else {
      setIsNoRacesPopupShowing(false);
    }
  }, [loading, filteredVariantsExceptTime, track, product, selectedDate]);

  const getTotalAvailableSeats = useCallback(
    (filter: TPriceFilterOptions | TTimeFilter) => {
      return filteredVariantsMap[filter]?.filter((variant) => variant.availableForSale && variant.quantityAvailable > 0).length || 0;
    },
    [filteredVariantsMap]
  );

  const findNextAvailableFilter = useCallback(() => {
    const currentFilter = activeSortType === "time" ? timeFilter : priceFilter;
    const currentIndex = filterOptions.findIndex((option) => option.filter === currentFilter);

    // Check if current filter has availability
    if (getTotalAvailableSeats(currentFilter) > 0) {
      return currentFilter;
    }

    // Search forward from current position
    for (let i = currentIndex + 1; i < filterOptions.length; i++) {
      if (getTotalAvailableSeats(filterOptions[i].filter) > 0) {
        return filterOptions[i].filter;
      }
    }

    // If not found forward, search backwards from current position
    for (let i = currentIndex - 1; i >= 0; i--) {
      if (getTotalAvailableSeats(filterOptions[i].filter) > 0) {
        return filterOptions[i].filter;
      }
    }

    // If no available filters found, return current filter
    return currentFilter;
  }, [activeSortType, timeFilter, priceFilter, filterOptions, getTotalAvailableSeats]);

  // Update filter when sort type, date, or variants change
  useEffect(() => {
    const nextFilter = findNextAvailableFilter();
    if (activeSortType === "price") {
      setPriceFilter(nextFilter as TPriceFilterOptions);
    } else {
      setTimeFilter(nextFilter as TTimeFilter);
    }
  }, [activeSortType, selectedDate, filteredVariantsMap, findNextAvailableFilter, track]);

  // Update selected variants when track changes
  useEffect(() => {
    const { variantsAndDate } =
      trackSelectionOption.title === "MAIN_TRACK"
        ? mainTrackSelection
        : trackSelectionOption.title === "MINI_TRACK"
        ? miniTrackSection
        : trackSelectionOption.title === "JUNIOR_TRACK"
        ? juniorTrackSelection
        : trackSelectionOption.title === "BATTLE_KART_TRACK"
        ? battleKartSelection
        : vrSelection;

    if (Array.isArray(variantsAndDate.selectedVariants)) {
      setSelectedVariants(variantsAndDate.selectedVariants.map((v) => v.variantsSelectedObject));
    }
  }, [trackSelectionOption, mainTrackSelection, miniTrackSection, vrSelection, juniorTrackSelection, battleKartSelection]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (touchTimeoutRef.current) {
        clearTimeout(touchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const fetchCartData = async () => {
      // only fetch shopify cart content once
      if (!isShopifyCartVariantsLoaded) {
        dispatch({
          type: "SET_IS_SHOPIFY_CART_VARIANTS_LOADING",
          payload: {
            isShopifyCartVariantsLoaded: true,
          },
        });
        const cartData = await getCartData(productRenderedLocation, headlessCartInfo);
        let lineItems: any[] = [];
        let productsInfoMap: Map<string, Set<string>> = new Map();
        let getProductsVariants: IGVariant[] = [];

        if (cartData) {
          const { cart } = cartData;
          if (cart) {
            const { items } = cart;
            if (items && items.length > 0) {
              const { items_subtotal_price } = cart;

              if (items_subtotal_price) {
                dispatch({
                  type: "SET_HEADLESS_CART_TOTAL_AMOUNT",
                  payload: {
                    headlessCartTotalAmount: items_subtotal_price,
                  },
                });
              }

              lineItems = items;
            }
          }
        }

        if (lineItems.length > 0) {
          lineItems.forEach((lineItem) => {
            const { handle, variant_id } = lineItem;
            if (handle && variant_id) {
              if (!productsInfoMap.has(handle)) {
                productsInfoMap.set(handle, new Set());
              }
              productsInfoMap.get(handle)?.add(variant_id);
            }
          });
        }

        // Convert Map to the desired array structure
        const productsInfo: { productHandle: string; productVariantIds: number[] }[] = Array.from(productsInfoMap.entries()).map(([productHandle, variantIdsSet]) => ({
          productHandle,
          productVariantIds: Array.from(variantIdsSet).map((id) => Number(id)),
        }));

        if (productsInfo.length > 0) {
          const getProducts = await Promise.all(productsInfo.map((productInfo) => fetchProductByHandle(productInfo.productHandle)));

          // Filter out null values and get the first valid product
          const validProducts = getProducts.filter((product): product is IGProduct => product !== null);

          if (validProducts.length > 0) {
            // Go through all products and their variants
            validProducts.forEach((product, productIndex) => {
              const { variants } = product;
              const currentProductInfo = productsInfo[productIndex];

              // Find all variants that match the current product's variant IDs
              variants.forEach((variant: IGVariant) => {
                const adminId = getAdminIdFromStoreFrontId(variant.id);
                if (currentProductInfo.productVariantIds.includes(adminId)) {
                  getProductsVariants.push(variant);
                }
              });
            });
          }
        }

        if (getProductsVariants.length > 0) {
          const findAllMainTrackVariants: TSelectedVariants[] = getProductsVariants
            .filter((variant) => variant.product.productType === "Main Track")
            .map((variant) => ({
              variantSelectedTime: parseDateFromProductTitle(variant.product.title),
              variantsSelectedObject: variant,
            }));

          const findAllMiniTrackVariants: TSelectedVariants[] = getProductsVariants
            .filter((variant) => variant.product.productType === "Mini Track")
            .map((variant) => ({
              variantSelectedTime: parseDateFromProductTitle(variant.product.title),
              variantsSelectedObject: variant,
            }));

          const findAllVirtualRealityVariants: TSelectedVariants[] = getProductsVariants
            .filter((variant) => variant.product.productType === "Virtual Reality")
            .map((variant) => ({
              variantSelectedTime: parseDateFromProductTitle(variant.product.title),
              variantsSelectedObject: variant,
            }));

          const findAllJuniorTrackVariants: TSelectedVariants[] = getProductsVariants
            .filter((variant) => variant.product.productType === "Junior Track")
            .map((variant) => ({
              variantSelectedTime: parseDateFromProductTitle(variant.product.title),
              variantsSelectedObject: variant,
            }));

          const findAllBattleKartVariants: TSelectedVariants[] = getProductsVariants
            .filter((variant) => variant.product.productType === "BattleKart")
            .map((variant) => ({
              variantSelectedTime: parseDateFromProductTitle(variant.product.title),
              variantsSelectedObject: variant,
            }));

          dispatch({
            type: "SET_SHOPIFY_CART_VARIANTS",
            payload: {
              shopifyCartVariants: [
                ...findAllMainTrackVariants,
                ...findAllMiniTrackVariants,
                ...findAllVirtualRealityVariants,
                ...findAllJuniorTrackVariants,
                ...findAllBattleKartVariants,
              ],
            },
          });
        }
      }
    };
    fetchCartData();
  }, [dispatch, productRenderedLocation, headlessCartInfo, isShopifyCartVariantsLoaded]);

  return (
    <>
      {loading ? (
        <>
          <>
            <div className="container tw-flex tw-justify-center tw-items-center tw-mt-[100px] tw-mb-[100px]">
              <img
                src={track === "BATTLE_KART_TRACK" ? BattleKartLogoURL : LoadingAnimationURL}
                alt={track === "BATTLE_KART_TRACK" ? "Battle Kart Loading" : ""}
                className="tw-w-[230px] md:tw-w-[300px]"
              />
            </div>
          </>
        </>
      ) : (
        <>
          <NoRacesPopup
            isPopupShowing={isNoRacesPopupShowing}
            handleClosePopup={() => {
              setIsNoRacesPopupShowing(false);
            }}
            ageFilter={ageFilter}
            track={track}
            numberOfSeats={getNumberOfSelectedSeats}
            numberOfRaces={getNumberOfSelectedRaces}
          />

          <div
            className={`${productRenderedLocation === "external" ? "tw-container tw-px-[10px] xl:tw-px-[0]" : "container"} tw-mx-auto ${
              selectProductOnly ? "tw-flex tw-flex-col tw-items-center" : "tw-grid tw-mt-[5px] lg:tw-mt-[40px] xl:tw-items-start"
            } ${appHostedAt && appHostedAt === "bosshunting" ? "tw-grid-cols-1 tw-gap-[10px]" : "tw-grid-cols-1 xl:tw-grid-cols-2"}`}
            id="react-component-product"
          >
            {selectProductOnly ? (
              <div className="tw-flex tw-justify-center tw-items-center tw-text-white tw-relative tw-mb-4 tw-bg-[#393B41] tw-py-1 tw-px-2 tw-rounded-[15px]">
                <span className="tw-font-[700] tw-text-[20px] tw-mr-3">{formattedDateString}</span>
                <CalendarIcon
                  className="tw-w-8 tw-h-8 tw-cursor-pointer tw-text-[#9d62fe]"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDesktopCalendar(!showDesktopCalendar);
                  }}
                />

                {showDesktopCalendar && (
                  <div
                    className="tw-absolute tw-rounded-[15px] tw-shadow-md tw-z-50 tw-top-[45px] tw-left-1/2 tw-transform tw-translate-x-[-50%] tw-bg-[#393B41]"
                    ref={calenderRef}
                  >
                    <div className="tw-w-full tw-flex tw-justify-center tw-items-start">
                      <div className="tw-overflow-hidden tw-rounded-[15px] tw-w-full">
                        <div className="tw-bg-[#393B41] tw-pt-[10px] tw-pr-[10px] tw-flex tw-items-center tw-justify-between tw-px-[10px]">
                          <span className={`tw-font-[700] tw-text-center tw-text-white tw-text-[20px] ${track === "BATTLE_KART_TRACK" ? "add-grobold-font" : ""}`}>
                            {DateTime.fromJSDate(tempSelectedMonth).toFormat("MMMM yyyy")}
                          </span>
                          <div className="tw-flex tw-items-center">
                            <ChevronLeftIcon
                              className={`tw-w-[30px] tw-h-[30px] tw-cursor-pointer ${
                                track === "BATTLE_KART_TRACK" ? "tw-text-[#EA581A]" : "tw-text-[#9d62fe]"
                              } ${dateDiff <= 0 ? "tw-opacity-50 tw-pointer-events-none" : ""}`}
                              onClick={() => handlePrevMonthClick()}
                            />
                            <ChevronRightIcon className={`tw-w-[30px] tw-h-[30px] tw-cursor-pointer ${
                              track === "BATTLE_KART_TRACK" ? "tw-text-[#EA581A]" : "tw-text-[#9d62fe]"
                            } tw-ml-2`} onClick={() => handleNextMonthClick()} />
                            <XIcon className="tw-w-5 tw-h-5 tw-cursor-pointer tw-text-white tw-ml-2" onClick={() => setShowDesktopCalendar(false)} />
                          </div>
                        </div>
                        <Calendar
                          className={`calender tw-m-auto ${track === "BATTLE_KART_TRACK" ? "battle-kart" : ""}`}
                          date={tempSelectedMonth}
                          onChange={handleSelect}
                          showMonthAndYearPickers={false}
                          minDate={DateTime.local().toJSDate()}
                          disabledDates={disableVariantsDate}
                          weekStartsOn={1}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="tw-block xl:tw-hidden">
                  <div className="tw-flex tw-flex-row tw-justify-between tw-items-center tw-gap-3 tw-mb-2">
                    <div
                      className={`tw-flex-1 tw-text-center font-race-bg tw-py-[8px] tw-px-[16px] tw-bg-[#393B41] tw-rounded-[15px] tw-flex tw-items-center tw-relative`}
                      style={{ letterSpacing: "0.01rem" }}
                    >
                      <ChevronLeftIcon
                        className={`tw-w-6 tw-h-6 tw-cursor-pointer ${
                          track === "BATTLE_KART_TRACK" ? "tw-text-[#EA581A]" : "tw-text-[#9d62fe]"
                        } ${
                          selectedDate ? (DateTime.fromJSDate(selectedDate).startOf("day") <= DateTime.local().startOf("day") ? "tw-opacity-50 tw-pointer-events-none" : "") : ""
                        }`}
                        onClick={handlePrevDayClick}
                      />
                      <div
                        className="tw-flex tw-justify-center tw-items-center tw-cursor-pointer tw-text-left tw-w-full"
                        onClick={(e) => {
                          // Stop propagation to prevent closing the calendar
                          e.stopPropagation();

                          if (showDateRange) {
                            setShowDateRange(false);
                          } else {
                            setTimeout(() => setShowDateRange(true), 200);
                          }
                        }}
                      >
                        <span className={`tw-text-white font-race-bg tw-font-medium tw-mx-auto ${track === "BATTLE_KART_TRACK" ? "add-grobold-font" : ""}`}>
                          {formattedDateString}
                        </span>
                      </div>
                      <ChevronRightIcon className={`tw-w-6 tw-h-6 tw-cursor-pointer ${
                        track === "BATTLE_KART_TRACK" ? "tw-text-[#EA581A]" : "tw-text-[#9d62fe]"
                      }`} onClick={handleNextDayClick} />
                      {showDateRange && (
                        <div
                          className="tw-fixed tw-rounded-[15px] tw-shadow-md tw-z-50 tw-left-1/2 tw-transform tw-translate-x-[-50%] tw-top-[200px] tw-flex tw-justify-center tw-items-start tw-bg-[#393B41] filter"
                          ref={calenderRef}
                        >
                          <div className="arrow is-triangle is-top tw-absolute tw-left-[80px] tw-top-[-10px]"></div>
                          <div className=" tw-overflow-hidden tw-rounded-[15px]">
                            <div className="tw-bg-[#393B41] tw-pt-[10px] tw-pr-[10px] tw-flex tw-items-center tw-justify-end">
                              <XIcon className="tw-w-5 tw-cursor-pointer tw-text-white" onClick={() => setShowDateRange(false)} />
                            </div>
                            <div className="tw-bg-[#393B41] tw-flex tw-justify-center tw-items-center tw-py-[14px] tw-px-[10px]">
                              <ChevronLeftIcon
                                className={`tw-w-[40px] tw-h-[40px] tw-rounded-[8px] tw-py-[4px] tw-px-[8px] tw-cursor-pointer ${
                                  track === "BATTLE_KART_TRACK" ? "tw-bg-[#EA581A26] tw-text-[#EA581A]" : "tw-bg-[#9d62fe26] tw-text-[#9d62fe]"
                                } ${
                                  dateDiff <= 0 ? "tw-pointer-events-none" : ""
                                }`}
                                onClick={() => handlePrevMonthClick()}
                              />
                              <span className={`tw-font-[700] tw-text-center tw-w-full tw-text-white tw-text-[20px] ${track === "BATTLE_KART_TRACK" ? "add-grobold-font" : ""}`}>
                                {DateTime.fromJSDate(tempSelectedMonth).toFormat("MMMM yyyy")}
                              </span>
                              <ChevronRightIcon
                                className={`tw-w-[40px] tw-h-[40px] tw-rounded-[8px] tw-py-[4px] tw-px-[8px] tw-cursor-pointer ${
                                  track === "BATTLE_KART_TRACK" ? "tw-bg-[#EA581A26] tw-text-[#EA581A]" : "tw-bg-[#9d62fe26] tw-text-[#9d62fe]"
                                } tw-ml-auto`}
                                onClick={() => handleNextMonthClick()}
                              />
                            </div>
                            <Calendar
                              className={`calender tw-m-auto ${track === "BATTLE_KART_TRACK" ? "battle-kart" : ""}`}
                              date={tempSelectedMonth}
                              onChange={handleSelect}
                              showMonthAndYearPickers={false}
                              minDate={DateTime.local().toJSDate()}
                              disabledDates={disableVariantsDate}
                              weekStartsOn={1}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                    {!selectProductOnly && (
                      <div className="tw-flex tw-flex-row tw-items-center tw-gap-2">
                        <div className="tw-text-white tw-text-[12px]">Sort</div>
                        <div className="tw-flex tw-bg-[#393B41] tw-rounded-full tw-p-[4px] tw-items-center tw-gap-[4px]">
                          <button
                            onClick={() => {
                              setActiveSortType("price");
                              logGAEvent("select_sort_option", {
                                event_category: "ProductSection_Interaction",
                                event_label: "Sort by Price",
                                selected_track: track,
                                selected_date: selectedDate,
                              });
                            }}
                            className={`tw-flex tw-items-center tw-justify-center tw-w-[40px] tw-h-[32px] tw-rounded-full tw-text-white tw-text-[12px] tw-cursor-pointer ${
                              activeSortType === "price" ? (track === "BATTLE_KART_TRACK" ? "tw-bg-[#EA581A]" : "bg-gradient-3") : "tw-bg-transparent"
                            }`}
                          >
                            <CurrencyDollarIcon className="tw-w-[16px] tw-h-[16px]" />
                          </button>
                          <button
                            onClick={() => setActiveSortType("time")}
                            className={`tw-flex tw-items-center tw-justify-center tw-w-[40px] tw-h-[32px] tw-rounded-full tw-text-white tw-text-[12px] tw-cursor-pointer ${
                              activeSortType === "time" ? (track === "BATTLE_KART_TRACK" ? "tw-bg-[#EA581A]" : "bg-gradient-3") : "tw-bg-transparent"
                            }`}
                          >
                            <ClockIcon className="tw-w-[16px] tw-h-[16px]" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="tw-hidden xl:tw-block">
                  <div className="tw-flex tw-flex-row tw-gap-[12px]">
                    <div
                      className={`tw-w-full md:tw-max-w-[550px] tw-mx-auto tw-text-center font-race-bg tw-bg-[#393b41] tw-text-white tw-rounded-[16px] tw-mt-[48px]`}
                      style={{ letterSpacing: "0.01rem" }}
                    >
                      <div className="tw-flex tw-items-center tw-text-center tw-justify-center tw-pt-[30px] tw-pb-[25px] tw-px-[40px]">
                        <ChevronLeftIcon
                          className={`width-6 tw-cursor-pointer ${dateDiff <= 0 ? "tw-text-[#C2C0C3] tw-pointer-events-none" : track === "BATTLE_KART_TRACK" ? "tw-text-[#EA581A]" : "tw-text-white"}`}
                          onClick={handlePrevMonthClick}
                        />
                        <div className="tw-cursor-pointer tw-w-full">
                          <span
                            className={`tw-flex tw-justify-center tw-items-center tw-text-white tw-text-[30px] tw-font-[700] ${
                              track === "BATTLE_KART_TRACK" ? "add-grobold-font" : ""
                            }`}
                          >
                            {DateTime.fromJSDate(tempSelectedMonth).toFormat("MMMM yyyy")}
                          </span>
                        </div>
                        <ChevronRightIcon className={`width-6 tw-cursor-pointer ${track === "BATTLE_KART_TRACK" ? "tw-text-[#EA581A]" : "tw-text-white"} tw-ml-auto`} onClick={handleNextMonthClick} />
                      </div>

                      <div className="tw-w-full tw-flex tw-justify-center tw-items-start">
                        <div className=" tw-overflow-hidden tw-rounded-[15px] tw-w-full">
                          <Calendar
                            className={`calender tw-m-auto ${track === "BATTLE_KART_TRACK" ? "battle-kart" : ""}`}
                            date={tempSelectedMonth}
                            onChange={handleSelect}
                            showMonthAndYearPickers={false}
                            minDate={DateTime.local().toJSDate()}
                            disabledDates={disableVariantsDate}
                            weekStartsOn={1}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
            {/* Giving full width for select product only */}
            <div
              className={`tw-flex tw-justify-center tw-flex-col tw-items-center tw-gap-[8px] tw-min-h-[300px] tw-w-full tw-mx-auto xl:tw-mx-0 tw-pb-[15px] lg:tw-pb-[0px] ${
                selectProductOnly ? "" : "md:tw-max-w-[550px]"
              }`}
            >
              {!selectProductOnly && (
                <div className="tw-hidden xl:tw-flex tw-w-full tw-flex-row tw-justify-end tw-items-center tw-gap-2">
                  <div className="tw-text-white tw-text-[12px]">Sort</div>
                  <div className="tw-flex tw-bg-[#393B41] tw-rounded-full tw-p-[4px] tw-items-center tw-gap-[4px]">
                    <GoogleAnalytics />
                    <button
                      onClick={() => {
                        setActiveSortType("price");
                        logGAEvent("select_sort_option", {
                          event_category: "ProductSection_Interaction",
                          event_label: "Sort by Price",
                          selected_track: track,
                          selected_date: selectedDate,
                        });
                      }}
                      className={`tw-flex tw-items-center tw-justify-center tw-w-[40px] tw-h-[32px] tw-rounded-full tw-text-white tw-text-[12px] tw-cursor-pointer ${
                        activeSortType === "price" ? (track === "BATTLE_KART_TRACK" ? "tw-bg-[#EA581A]" : "bg-gradient-3") : "tw-bg-transparent"
                      }`}
                    >
                      <CurrencyDollarIcon className="tw-w-[16px] tw-h-[16px]" />
                    </button>
                    <button
                      onClick={() => setActiveSortType("time")}
                      className={`tw-flex tw-items-center tw-justify-center tw-w-[40px] tw-h-[32px] tw-rounded-full tw-text-white tw-text-[12px] tw-cursor-pointer ${
                        activeSortType === "time" ? (track === "BATTLE_KART_TRACK" ? "tw-bg-[#EA581A]" : "bg-gradient-3") : "tw-bg-transparent"
                      }`}
                    >
                      <ClockIcon className="tw-w-[16px] tw-h-[16px]" />
                    </button>
                    {/* Commented out for now upon feedback from client */}
                    {/* {track === "BATTLE_KART_TRACK" && (
                      <button
                        onClick={() => setActiveSortType("preset")}
                        className={`tw-flex tw-items-center tw-gap-[4px] tw-px-[12px] tw-py-[4px] tw-rounded-full tw-text-white tw-text-[12px] tw-cursor-pointer ${
                          activeSortType === "preset" ? "tw-bg-[#EA581A]" : "tw-bg-transparent"
                        }`}
                      >
                        <CogIcon className="tw-w-[16px] tw-h-[16px]" />
                        <span>Preset</span>
                      </button>
                    )} */}
                  </div>
                </div>
              )}
              <div className="tw-w-full tw-rounded-[15px] tw-bg-[#555758] tw-relative tw-flex tw-flex-col tw-flex-1">
                <div className="tw-flex tw-flex-row tw-text-center tw-gap-[5px] tw-p-[5px] tw-rounded-[15px] tw-bg-[#393B41]">
                  {filterOptions.map((option) => (
                    <FilterBoxHeader
                      key={option.filter}
                      title={option.title}
                      subTitle={"subTitle" in option ? option.subTitle : undefined}
                      isActive={option.active}
                      onClick={handleFilterOptionClick(option)}
                      numAvailableSeats={getTotalAvailableSeats(option.filter)}
                      track={track}
                    />
                  ))}
                </div>

                {product?.variants?.length === 0 ? (
                  <p className="tw-text-white tw-text-center tw-font-[600] tw-py-[30px] lg:tw-py-[0px] lg:tw-absolute lg:tw-top-[56%] lg:tw-left-[50%] lg:tw-translate-y-[-50%] lg:tw-translate-x-[-50%]">
                    Sessions not available yet
                  </p>
                ) : (
                  <>
                    <div className="tw-pt-[12px] tw-pb-[30px] tw-px-[2px] md:tw-px-[10px]">
                      <Swiper
                        key={`${activeSortType}-${selectedDate?.getTime()}`}
                        modules={[Navigation, Pagination]}
                        spaceBetween={10}
                        slidesPerView={1}
                        autoHeight={true}
                        initialSlide={getInitialSlideIndex()}
                        allowTouchMove={true}
                        touchRatio={1}
                        resistance={true}
                        resistanceRatio={0.85}
                        updateOnWindowResize={true}
                        speed={300}
                        onSwiper={(swiper: SwiperType) => {
                          swiperRef.current = swiper;
                        }}
                        onSlideChange={(swiper: SwiperType) => {
                          // Simple slide change handler without complex state updates
                          const newIndex = swiper.activeIndex;
                          const availableSlides = filterOptions
                            .map((filterOption) => {
                              const variantsForThisFilter = filteredVariantsMap[filterOption.filter] || [];
                              return {
                                filter: filterOption.filter,
                                hasContent: variantsForThisFilter.length > 0 && variantsForThisFilter.some((variant) => variant.availableForSale),
                              };
                            })
                            .filter((slide) => slide.hasContent);

                          const newFilter = availableSlides[newIndex]?.filter;
                          if (newFilter) {
                            if (activeSortType === "price") {
                              setPriceFilter(newFilter as TPriceFilterOptions);
                            } else {
                              setTimeFilter(newFilter as TTimeFilter);
                            }
                          }
                        }}
                        onTouchMove={(swiper: SwiperType) => {
                          console.log("onTouchMove", swiper);

                          // Set touching state to true when touch moves
                          if (!isTouching) {
                            setIsTouching(true);
                            // Store initial touch position
                            touchStartXRef.current = swiper.touches.startX;
                          }

                          // Update current touch position
                          touchCurrentXRef.current = swiper.touches.currentX;

                          // Clear any existing timeout
                          if (touchTimeoutRef.current) {
                            clearTimeout(touchTimeoutRef.current);
                          }

                          // Set a timeout to detect when touch ends
                          // If no more touchMove events fire within 100ms, we consider the touch ended
                          touchTimeoutRef.current = setTimeout(() => {
                            // console.log("Touch ended detected via timeout");
                            setIsTouching(false);

                            // Calculate touch direction and distance
                            if (touchStartXRef.current !== null && touchCurrentXRef.current !== null && swiperRef.current) {
                              const touchDistance = touchCurrentXRef.current - touchStartXRef.current;
                              const minSwipeDistance = 50; // Minimum distance to trigger slide change

                              // console.log("Touch distance:", touchDistance);

                              if (Math.abs(touchDistance) > minSwipeDistance) {
                                // Determine direction and update slide accordingly
                                if (touchDistance > 0) {
                                  // Swiped right - go to previous slide
                                  // console.log("Swiped right - going to previous slide");
                                  if (swiperRef.current.activeIndex > 0) {
                                    swiperRef.current.slidePrev();
                                  }
                                } else {
                                  // Swiped left - go to next slide
                                  // console.log("Swiped left - going to next slide");
                                  const totalSlides = swiperRef.current.slides.length;
                                  if (swiperRef.current.activeIndex < totalSlides - 1) {
                                    swiperRef.current.slideNext();
                                  }
                                }
                              }

                              // Reset touch positions
                              touchStartXRef.current = null;
                              touchCurrentXRef.current = null;
                            }

                            // Force update the swiper after touch ends
                            if (swiperRef.current) {
                              swiperRef.current.update();
                              swiperRef.current.updateSlides();
                              swiperRef.current.updateProgress();
                            }
                          }, 100);
                        }}
                        pagination={{
                          clickable: true,
                          el: ".swiper-pagination",
                        }}
                        className="tw-bg-[#555758] tw-rounded-[15px] tw-overflow-hidden"
                        style={
                          {
                            "--swiper-navigation-color": "#9d62fe",
                            "--swiper-pagination-color": "#EA581A",
                            "--swiper-pagination-bullet-inactive-color": "#666666",
                            "--swiper-pagination-bullet-inactive-opacity": "0.5",
                            "--swiper-transition-timing-function": "ease-out",
                          } as React.CSSProperties
                        }
                      >
                        {filterOptions
                          .filter((option) => {
                            // Only show slides for filters that have variants AND at least some variants are available for sale
                            const variantsForThisFilter = filteredVariantsMap[option.filter] || [];
                            return variantsForThisFilter.length > 0 && variantsForThisFilter.some((variant) => variant.availableForSale);
                          })
                          .map((option) => {
                            const variantsForThisFilter = filteredVariantsMap[option.filter] || [];
                            const variantsWithSelection = variantsForThisFilter.map((variant) => ({
                              ...variant,
                              selected: selectedVariants.some((v) => v.id === variant.id),
                            }));

                            return (
                              <SwiperSlide key={option.filter}>
                                <div
                                  className={`tw-grid ${
                                    activeSortType === "price"
                                      ? `${
                                          track === "MAIN_TRACK" || track === "JUNIOR_TRACK" || track === "MINI_TRACK" || track === "BATTLE_KART_TRACK"
                                            ? "tw-grid-cols-4"
                                            : "tw-grid-cols-5"
                                        }`
                                      : `${
                                          track === "MAIN_TRACK" || track === "JUNIOR_TRACK" || track === "MINI_TRACK"
                                            ? "tw-grid-cols-4"
                                            : track === "BATTLE_KART_TRACK"
                                            ? "tw-grid-cols-4"
                                            : "tw-grid-cols-3"
                                        }`
                                  } tw-grid-cols-${
                                    activeSortType === "price"
                                      ? track === "MAIN_TRACK" || track === "JUNIOR_TRACK" || track === "MINI_TRACK" || track === "BATTLE_KART_TRACK"
                                        ? noOfCells
                                        : noOfCells + 1
                                      : noOfCells
                                  } tw-gap-[1px] tw-bg-[#555758]`}
                                >
                                  {variantsWithSelection.map((variant) => (
                                    <VariantsBlock
                                      key={variant.id}
                                      variant={variant}
                                      getRaceTimeForVariant={getRaceTimeForVariant}
                                      selectedVariants={selectedVariants}
                                      onClick={variantBlockClick(variant)}
                                      activeRace={activeRace}
                                      sessionDisplaytime={timeFilter}
                                      formattedDateString={selectedDate as Date}
                                    />
                                  ))}
                                </div>
                              </SwiperSlide>
                            );
                          })}
                      </Swiper>
                    </div>
                    {(() => {
                      // Check if there are any slides with content
                      const hasSlidesWithContent = filterOptions.some((option) => {
                        const variantsForThisFilter = filteredVariantsMap[option.filter] || [];
                        return variantsForThisFilter.length > 0 && variantsForThisFilter.some((variant) => variant.availableForSale);
                      });

                      return !hasSlidesWithContent ? (
                        <p className="tw-text-white tw-text-center tw-font-[600] tw-py-[30px] lg:tw-py-[0px] lg:tw-absolute lg:tw-top-[56%] lg:tw-left-[50%] lg:tw-translate-y-[-50%] lg:tw-translate-x-[-50%]">
                          No sessions available for this {activeSortType === "price" ? "price" : "time"} range
                        </p>
                      ) : null;
                    })()}
                  </>
                )}
                {(() => {
                  // Check if there are any slides with content
                  const hasSlidesWithContent = filterOptions.some((option) => {
                    const variantsForThisFilter = filteredVariantsMap[option.filter] || [];
                    return variantsForThisFilter.length > 0 && variantsForThisFilter.some((variant) => variant.availableForSale);
                  });

                  return hasSlidesWithContent ? (
                    <div
                      className={`swiper-pagination !tw-bottom-[4px] ${track === "BATTLE_KART_TRACK" ? "swiper-pagination-battle-kart" : "swiper-pagination-primary-track"}`}
                    ></div>
                  ) : null;
                })()}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
